/**
 * CampaignOverrideStore — Manages campaign map overrides.
 *
 * Dev edits a campaign map in the editor → saves override here → syncs to
 * `public/campaign-overrides.json` via Vite dev server (same pattern as ProfileSync).
 * At boot the file is fetched and cached in memory.
 * When loading a campaign level, check here first; if no override, generate from seed.
 */
import type { MapDefinition } from '@/map/MapDefs';

const API_ENDPOINT = '/api/save-campaign-overrides';
const PUBLIC_FILE = '/campaign-overrides.json';

class CampaignOverrideStoreClass {
  private overrides: Record<string, MapDefinition> = {};
  private loaded = false;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;

  /** Fetch campaign-overrides.json once at boot, cache in memory. */
  async load(): Promise<void> {
    if (this.loaded) return;
    try {
      const resp = await fetch(PUBLIC_FILE);
      if (!resp.ok) { this.loaded = true; return; } // 404 = no overrides yet
      const json = await resp.json();
      if (json && typeof json === 'object') {
        this.overrides = json as Record<string, MapDefinition>;
      }
    } catch (e) { console.warn('[CampaignOverrideStore] load failed:', e); }
    this.loaded = true;
  }

  /** Get override for a level ID, or undefined if none. */
  get(levelId: string): MapDefinition | undefined {
    return this.overrides[levelId];
  }

  /** Check if an override exists for this level. */
  has(levelId: string): boolean {
    return levelId in this.overrides;
  }

  /** Save an override and sync to dev server. */
  save(levelId: string, map: MapDefinition): void {
    this.overrides[levelId] = JSON.parse(JSON.stringify(map)) as MapDefinition;
    this._scheduleSync();
  }

  /** Remove an override (revert to procedural). Syncs to dev server. */
  revert(levelId: string): void {
    delete this.overrides[levelId];
    this._scheduleSync();
  }

  /** Get all overridden level IDs. */
  getOverriddenIds(): string[] {
    return Object.keys(this.overrides);
  }

  /** Number of active overrides. */
  get count(): number {
    return Object.keys(this.overrides).length;
  }

  // ── Dev server sync (debounced) ──

  private _scheduleSync(): void {
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      this._syncToDevServer();
    }, 500);
  }

  private _syncToDevServer(): void {
    const json = JSON.stringify(this.overrides, null, 2);
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
    }).catch(() => { /* silent if not in dev */ });
  }
}

export const CampaignOverrideStore = new CampaignOverrideStoreClass();
